import React, { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  FaHeart,
  FaTrashAlt,
  FaShoppingCart,
  FaArrowLeft,
} from "react-icons/fa";
import { toast } from "react-toastify";
import "./Wishlist.css";
import { wishlistApi } from "../api/services";
import { getErrorMessage } from "../api/client";
import { useAuth } from "../context/AuthContext";
import { useCart } from "../context/CartContext";
import { useWishlist } from "../context/WishlistContext";
import { resolveAssetUrl } from "../utils/assets";
import Seo from "../components/Seo";
import { PAGE_SEO } from "../constants/seo";
import { CardGridSkeleton } from "../components/Skeleton";

const Wishlist = () => {
  const navigate = useNavigate();
  const { isAuthenticated, loading: authLoading } = useAuth();
  const { refreshCart } = useCart();
  const { setWishlistCount } = useWishlist();
  const [wishlistItems, setWishlistItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const loadWishlist = useCallback(async () => {
    setLoading(true);
    try {
      const res = await wishlistApi.get();
      const items = res.data.data.wishlist?.items || [];
      setWishlistItems(items);
      setWishlistCount(items.length);
    } catch (error) {
      toast.error(getErrorMessage(error, "Failed to load wishlist"));
      setWishlistItems([]);
      setWishlistCount(0);
    } finally {
      setLoading(false);
    }
  }, [setWishlistCount]);

  useEffect(() => {
    if (authLoading) return;
    if (!isAuthenticated) {
      navigate("/login", { state: { from: "/wishlist" } });
      return;
    }
    loadWishlist();
  }, [authLoading, isAuthenticated, navigate, loadWishlist]);

  const removeItem = async (id) => {
    setBusy(true);
    try {
      const res = await wishlistApi.remove(id);
      const items = res.data.data.wishlist?.items || [];
      setWishlistItems(items);
      setWishlistCount(items.length);
      toast.success("Removed from wishlist");
    } catch (error) {
      toast.error(getErrorMessage(error, "Could not remove item"));
    } finally {
      setBusy(false);
    }
  };

  const addToCart = async (id) => {
    setBusy(true);
    try {
      await wishlistApi.moveToCart(id);
      await refreshCart();
      setWishlistItems((items) => {
        const next = items.filter((item) => item.id !== id);
        setWishlistCount(next.length);
        return next;
      });
      toast.success("Moved to cart");
    } catch (error) {
      toast.error(getErrorMessage(error, "Could not add to cart"));
    } finally {
      setBusy(false);
    }
  };

  if (authLoading || loading) {
    return (
      <main className="wishlist-page">
        <Seo {...PAGE_SEO.wishlist} />
        <section className="wishlist-hero">
          <span className="wishlist-badge">
            <FaHeart /> Wishlist
          </span>
          <h1>Your Saved Products</h1>
          <p>
            Keep your favourite office, stationery, and educational products saved
            for later.
          </p>
        </section>
        <section className="wishlist-section">
          <CardGridSkeleton count={6} />
        </section>
      </main>
    );
  }

  return (
    <main className="wishlist-page">
      <Seo {...PAGE_SEO.wishlist} />
      <section className="wishlist-hero">
        <span className="wishlist-badge">
          <FaHeart /> Wishlist
        </span>

        <h1>Your Saved Products</h1>

        <p>
          Keep your favourite office, stationery, and educational products saved
          for later.
        </p>
      </section>

      <section className="wishlist-section">
        {wishlistItems.length === 0 ? (
          <div className="empty-wishlist">
            <FaHeart />
            <h2>Your wishlist is empty</h2>
            <p>Save products you like and view them here anytime.</p>

            <Link to="/products" className="wishlist-continue-btn">
              Continue Shopping
            </Link>
          </div>
        ) : (
          <>
            <div className="wishlist-title-row">
              <h2>Wishlist Items</h2>
              <span>{wishlistItems.length} Items</span>
            </div>

            <div className="wishlist-grid">
              {wishlistItems.map((item) => (
                <div className="wishlist-card" key={item.id}>
                  <div className="wishlist-image-box">
                    {item.image ? (
                      <img src={resolveAssetUrl(item.image)} alt={item.name} />
                    ) : (
                      <div
                        style={{
                          width: "100%",
                          height: "100%",
                          minHeight: 160,
                          background: "#eee",
                        }}
                      />
                    )}

                    <button
                      className="wishlist-remove"
                      disabled={busy}
                      onClick={() => removeItem(item.id)}
                    >
                      <FaTrashAlt />
                    </button>
                  </div>

                  <div className="wishlist-content">
                    <span>{item.category}</span>
                    <h3>{item.name}</h3>
                    <p>AED {Number(item.price).toFixed(2)}</p>

                    <button
                      className="wishlist-cart-btn"
                      disabled={busy}
                      onClick={() => addToCart(item.id)}
                    >
                      <FaShoppingCart />
                      Add to Cart
                    </button>
                  </div>
                </div>
              ))}
            </div>

            <Link to="/products" className="wishlist-back">
              <FaArrowLeft /> Continue Shopping
            </Link>
          </>
        )}
      </section>
    </main>
  );
};

export default Wishlist;
